import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { NavIcon } from '@/components/navIcons';
import { NAV_GROUP_ICONS, type NavItem } from '@/lib/navItems';

/**
 * The left menu, as Hub's (owner, 2026-09-21: "as like our hub side menu, hover, expand and
 * contract, sub menu, styles").
 *
 * EXPANDED: each group is a row - icon, name, chevron - that opens its screens beneath it on a
 * tree line. A group of one screen is simply that screen. The group holding the current page is
 * always open; the others remember whether they were opened, per browser.
 *
 * RAIL: one icon per group. Hovering (or focusing) a group icon flies its screens out beside the
 * rail, in a panel drawn outside the menu so the menu's scroll cannot clip it. The group holding
 * the current page is marked. The shell also widens the rail over the page while the pointer rests
 * on it, as Hub does; the fly-out is what serves a quick jump.
 */
const STORAGE_KEY = 'vanam.nav.open';

type Group = { group: string; items: NavItem[] };

export function SideNav({
  groups,
  onNavigate,
  railed = false,
}: {
  groups: Group[];
  onNavigate?: () => void;
  railed?: boolean;
}) {
  const location = useLocation();
  const activeKey = location.pathname.replace(/^\//, '').split('/')[0] ?? '';
  const groupOfActive = useMemo(
    () => groups.find((entry) => entry.items.some((item) => item.key === activeKey))?.group,
    [groups, activeKey],
  );

  const [opened, setOpened] = useState<string[]>(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored ? (JSON.parse(stored) as string[]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(opened)); } catch { /* a convenience only */ }
  }, [opened]);

  function toggle(group: string) {
    setOpened((current) => (current.includes(group) ? current.filter((entry) => entry !== group) : [...current, group]));
  }

  if (railed) {
    return (
      <nav aria-label="Main" className="side-nav is-rail">
        {groups.map((entry) => (
          <RailGroup active={entry.group === groupOfActive} activeKey={activeKey} entry={entry} key={entry.group} onNavigate={onNavigate} />
        ))}
      </nav>
    );
  }

  return (
    <nav aria-label="Main" className="side-nav">
      {groups.map(({ group, items }) => {
        const first = items[0];
        if (items.length === 1 && first) {
          return <Leaf item={first} key={group} onNavigate={onNavigate} top />;
        }
        const isActiveGroup = group === groupOfActive;
        const open = isActiveGroup || opened.includes(group);
        return (
          <div className={`side-group${isActiveGroup ? ' has-active' : ''}`} key={group}>
            <button aria-expanded={open} className="side-group-row" onClick={() => toggle(group)} type="button">
              <NavIcon name={NAV_GROUP_ICONS[group] ?? first?.icon ?? 'LayoutDashboard'} size={18} />
              <span className="side-label">{group}</span>
              <ChevronDown className={`side-chevron${open ? ' open' : ''}`} size={14} />
            </button>
            {open && (
              <div className="side-sub" role="group" aria-label={group}>
                {items.map((item, index) => {
                  // A subgroup is one row that flies its screens out beside the menu (owner,
                  // 2026-09-27: "can have this type of sidebar display as the current display is
                  // odd? for farm management and monitoring").
                  if (!item.subgroup) return <Leaf item={item} key={item.key} onNavigate={onNavigate} />;
                  if (item.subgroup === items[index - 1]?.subgroup) return null;
                  const members = items.filter((other) => other.subgroup === item.subgroup);
                  return <SubMenu activeKey={activeKey} items={members} key={`sub-${item.subgroup}`} name={item.subgroup} onNavigate={onNavigate} />;
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function Leaf({ item, onNavigate, top = false }: { item: NavItem; onNavigate?: () => void; top?: boolean }) {
  return (
    <NavLink className={({ isActive }) => `side-link${top ? ' is-top' : ''}${isActive ? ' active' : ''}`}
             onClick={onNavigate} to={`/${item.key}`}>
      <NavIcon name={item.icon} size={top ? 18 : 15} />
      <span className="side-label">{item.label}</span>
    </NavLink>
  );
}

/** A group on the rail: one icon; its screens fly out on hover or focus. */
/** A subgroup inside an open group: one row, its screens in a panel beside the menu. */
function SubMenu({ name, items, activeKey, onNavigate }: {
  name: string; items: NavItem[]; activeKey: string; onNavigate?: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [top, setTop] = useState<number | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const holdsActive = items.some((item) => item.key === activeKey);

  function show() {
    window.clearTimeout(closeTimer.current);
    if (ref.current) setTop(ref.current.getBoundingClientRect().top);
  }
  function hide() {
    closeTimer.current = window.setTimeout(() => setTop(null), 150);
  }
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  return (
    <div onBlur={hide} onFocus={show} onMouseEnter={show} onMouseLeave={hide}>
      <button aria-expanded={top !== null} aria-haspopup="true" className={`side-link side-submenu${holdsActive ? ' active' : ''}`}
              onClick={() => (top === null ? show() : setTop(null))} ref={ref} type="button">
        <NavIcon name={NAV_GROUP_ICONS[name] ?? items[0]?.icon ?? 'LayoutDashboard'} size={15} />
        <span className="side-label">{name}</span>
        <ChevronRight className="side-chevron" size={14} />
      </button>
      {top !== null && createPortal(
        <div className="rail-flyout" onMouseEnter={show} onMouseLeave={hide}
             style={{ top, left: (ref.current?.closest('aside')?.getBoundingClientRect().right ?? ref.current?.getBoundingClientRect().right ?? 240) + 6 }}>
          <div className="rail-flyout-title">{name}</div>
          {items.map((item) => (
            <NavLink className={`rail-flyout-link${item.key === activeKey ? ' active' : ''}`} key={item.key}
                     onClick={() => { setTop(null); onNavigate?.(); }} to={`/${item.key}`}>
              <NavIcon name={item.icon} size={14} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function RailGroup({ entry, active, activeKey, onNavigate }: {
  entry: Group; active: boolean; activeKey: string; onNavigate?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const single = entry.items.length === 1 ? entry.items[0] : undefined;

  function show() {
    window.clearTimeout(closeTimer.current);
    if (ref.current) setTop(ref.current.getBoundingClientRect().top);
  }
  // A short grace period lets the pointer travel from the icon into the panel.
  function hide() {
    closeTimer.current = window.setTimeout(() => setTop(null), 120);
  }
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const icon = <NavIcon name={single?.icon ?? NAV_GROUP_ICONS[entry.group] ?? 'LayoutDashboard'} size={18} />;
  return (
    <div className="rail-item" onBlur={hide} onFocus={show} onMouseEnter={show} onMouseLeave={hide} ref={ref}>
      {single ? (
        <NavLink aria-label={single.label} className={({ isActive }) => `rail-button${isActive ? ' active' : ''}`}
                 onClick={onNavigate} to={`/${single.key}`}>
          {icon}
        </NavLink>
      ) : (
        <button aria-haspopup="true" aria-label={entry.group} className={`rail-button${active ? ' active' : ''}`} type="button">
          {icon}
        </button>
      )}
      {top !== null && createPortal(
        <div className={`rail-flyout${single ? ' is-tip' : ''}`} onMouseEnter={show} onMouseLeave={hide}
             style={{ top, left: (ref.current?.closest('aside')?.getBoundingClientRect().right ?? ref.current?.getBoundingClientRect().right ?? 60) + 6 }}>
          <div className="rail-flyout-title">{single ? single.label : entry.group}</div>
          {!single && entry.items.map((item) => (
            <NavLink className={`rail-flyout-link${item.key === activeKey ? ' active' : ''}`} key={item.key}
                     onClick={() => { setTop(null); onNavigate?.(); }} to={`/${item.key}`}>
              <NavIcon name={item.icon} size={14} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
